import { forwardRef, useMemo } from 'react';
import { Breakpoint, useMediaQuery, useTheme } from '@mui/material';

import { Button, type ButtonProps } from './button';

export interface ResponsiveButtonProps extends ButtonProps {
  /**
   * Breakpoint below which the label is dropped and only the icon renders.
   * Defaults to `md`, so it's a full button on md and up.
   */
  collapseBelow?: Breakpoint;
  /**
   * Accessible name + tooltip used while collapsed. Falls back to `title`, then
   * to `content` when that's a plain string — so a button labelled with text
   * usually needs nothing extra.
   */
  label?: string;
}

/**
 * A {@link Button} that becomes an icon-only button on small screens.
 *
 * Toolbars and header rows are where mobile layouts break: each labelled button
 * carries a min-width that can't shrink, and a few of them together push the
 * page wider than the viewport. Dropping to icons reclaims that width without
 * losing the action.
 *
 * Requires an `icon` or `muiIcon` — with nothing to draw, a collapsed button
 * would be an empty tap target, so it stays a full button instead.
 *
 * @example
 * ```tsx
 * <ResponsiveButton
 *   content="Add Job"
 *   muiIcon={<Add />}
 *   variant="contained"
 *   onClick={handleAdd}
 * />
 * ```
 */
export const ResponsiveButton = forwardRef<
  HTMLButtonElement,
  ResponsiveButtonProps
>(({ collapseBelow = 'md', label, ...props }, ref) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(collapseBelow));

  const accessibleLabel = useMemo(() => {
    if (label) return label;
    if (props.title) return props.title;
    return typeof props.content === 'string' ? props.content : undefined;
  }, [label, props.title, props.content]);

  const hasIcon = Boolean(props.icon || props.muiIcon);

  if (!isSmallScreen || !hasIcon) {
    return <Button ref={ref} {...props} aria-label={props['aria-label']} />;
  }

  // Button renders an IconButton when `content` is empty — reuse that path so
  // the variant/colour/loading mapping stays in one place.
  //
  // The label rides along as `title` (native hover tooltip, for a pointer user
  // in a narrow window) and `aria-label` (the accessible name, since there's no
  // visible text left). Not a MUI Tooltip: it injects handlers and a ref onto
  // its child, and Button takes a fixed prop set rather than spreading the
  // rest, so they'd be silently dropped and the tooltip would never open.
  return (
    <Button
      ref={ref}
      {...props}
      content={undefined}
      title={props.title ?? accessibleLabel}
      aria-label={props['aria-label'] ?? accessibleLabel}
    />
  );
});

ResponsiveButton.displayName = 'ResponsiveButton';
